import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	Box,
	Chip,
	Divider,
	Drawer,
	IconButton,
	Link,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { extractReceivablesError, getAgingInvoices, getAgingOrders } from '../../services';
import { IAgingInvoice, IAgingOrder, Segmento, TipoOrden } from '../../types';
import { agencyName } from '../../constants';
import { formatFecha, usd } from '../../helpers';

/** Qué se está mirando: un bucket de facturas o un tipo de órdenes. */
export interface DrilldownTarget {
	titulo: string;
	subtitulo?: string;
	/** Uno de los dos: bucket de facturas o tipo de órdenes. */
	bucket?: string;
	tipoOrden?: TipoOrden;
	passengerId?: string;
	agencyId?: number;
}

interface AgingDrilldownProps {
	target: DrilldownTarget | null;
	onClose: () => void;
	segments: Segmento[];
	asOf: string;
}

/** Días de atraso o que faltan, en criollo. */
function diasLabel(dias: number): { texto: string; color: string } {
	if (dias > 0) return { texto: `${dias} días vencida`, color: 'error.main' };
	if (dias === 0) return { texto: 'Vence hoy', color: 'warning.main' };
	return { texto: `Vence en ${-dias} días`, color: 'text.secondary' };
}

/**
 * Panel lateral con el detalle detrás de un número de la tabla.
 *
 * Es lo que convierte el informe en algo accionable: ver "USD 12.000 vencido"
 * no sirve si no podés llegar a las facturas y a las órdenes en un click.
 */
export function AgingDrilldown({ target, onClose, segments, asOf }: AgingDrilldownProps): JSX.Element {
	const [invoices, setInvoices] = useState<IAgingInvoice[] | null>(null);
	const [orders, setOrders] = useState<IAgingOrder[] | null>(null);
	const [total, setTotal] = useState(0);
	/** Cuántas filas hay en total contra cuántas trajimos: null = no hay tope. */
	const [corte, setCorte] = useState<{ mostradas: number; totales: number } | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!target) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setInvoices(null);
		setOrders(null);
		setCorte(null);

		const req = target.bucket
			? getAgingInvoices({
					bucket: target.bucket,
					passengerId: target.passengerId,
					agencyId: target.agencyId,
					segments,
					asOf,
			  }).then((d) => {
					if (cancelled) return;
					setInvoices(d.invoices);
					setTotal(d.total);
					if (d.truncated) setCorte({ mostradas: d.returned, totales: d.totalCount });
			  })
			: getAgingOrders({
					tipo: target.tipoOrden ?? 'COBRANZA',
					passengerId: target.passengerId,
					agencyId: target.agencyId,
					segments,
			  }).then((d) => {
					if (cancelled) return;
					setOrders(d.orders);
					// Cada tipo tiene su propio total: el saldo a favor viene del
					// pendiente de pago en negativo y hay que mostrarlo en positivo,
					// como en la tabla.
					setTotal(
						target.tipoOrden === 'FACTURAR'
							? d.totalPendienteFacturar
							: target.tipoOrden === 'SALDO_A_FAVOR'
							? d.totalSaldoAFavor
							: d.totalPendienteCobrar,
					);
					if (d.truncated) setCorte({ mostradas: d.returned, totales: d.totalCount });
			  });

		req
			.catch((err) => {
				if (!cancelled) setError(extractReceivablesError(err));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [target, segments, asOf]);

	const vacio = !loading && !error && (invoices?.length === 0 || orders?.length === 0);

	return (
		<Drawer anchor="right" open={Boolean(target)} onClose={onClose}>
			<Box sx={{ width: { xs: '100vw', sm: 720 }, p: 2.5 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
					<Box>
						<Typography variant="h6" fontWeight={700}>
							{target?.titulo}
						</Typography>
						{target?.subtitulo && (
							<Typography variant="body2" color="text.secondary">
								{target.subtitulo}
							</Typography>
						)}
					</Box>
					<IconButton size="small" onClick={onClose}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				{!loading && !error && <Chip label={usd(total)} size="small" color="primary" sx={{ mb: 2, fontWeight: 700 }} />}

				<Divider sx={{ mb: 2 }} />

				{loading ? (
					<Skeleton variant="rectangular" height={260} />
				) : error ? (
					<Alert severity="error">{error}</Alert>
				) : vacio ? (
					<Typography variant="body2" color="text.secondary">
						No hay nada para mostrar acá.
					</Typography>
				) : invoices ? (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Factura</TableCell>
									<TableCell>Vencimiento</TableCell>
									<TableCell align="right">Saldo</TableCell>
									<TableCell>Estado</TableCell>
									<TableCell>Orden</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{invoices.map((f) => {
									const d = diasLabel(f.dias);
									return (
										<TableRow key={f.invoiceId} hover>
											<TableCell>
												<Typography variant="body2" fontWeight={600}>
													{f.invoiceNumber}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Emitida {formatFecha(f.issueDate)}
												</Typography>
											</TableCell>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFecha(f.dueDate)}</TableCell>
											<TableCell align="right">
												<Typography variant="body2" fontWeight={600}>
													{usd(f.pendingUsd)}
												</Typography>
												{f.currency !== 'USD' && (
													<Typography variant="caption" color="text.secondary">
														{f.currency}{' '}
														{new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(f.pendingOriginal)}
													</Typography>
												)}
											</TableCell>
											<TableCell>
												<Typography variant="caption" color={d.color} fontWeight={600}>
													{d.texto}
												</Typography>
											</TableCell>
											<TableCell>
												<Link
													component={RouterLink}
													to={`/salesorder/${f.saleOrderId}`}
													target="_blank"
													rel="noopener"
													underline="hover"
													variant="caption"
													sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
												>
													{f.saleOrderId}
													<OpenInNewIcon sx={{ fontSize: 12 }} />
												</Link>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				) : orders ? (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Orden</TableCell>
									<TableCell>Agencia</TableCell>
									<TableCell align="right">Venta</TableCell>
									<TableCell align="right">Facturado</TableCell>
									<TableCell align="right">Pendiente</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{orders.map((o) => (
									<TableRow key={o.saleOrderId} hover>
										<TableCell>
											<Link
												component={RouterLink}
												to={`/salesorder/${o.saleOrderId}`}
												target="_blank"
												rel="noopener"
												underline="hover"
												variant="body2"
												sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
											>
												{o.saleOrderId}
												<OpenInNewIcon sx={{ fontSize: 12 }} />
											</Link>
											{o.clientesFacturables > 1 && (
												<Chip
													label={`${o.clientesFacturables} clientes facturables`}
													size="small"
													color="warning"
													variant="outlined"
													sx={{ ml: 0.5, fontSize: 10 }}
												/>
											)}
										</TableCell>
										<TableCell>
											<Typography variant="caption" color="text.secondary">
												{agencyName(o.agencyId)} · {o.segmentType}
											</Typography>
										</TableCell>
										<TableCell align="right">{usd(o.saleAmount)}</TableCell>
										<TableCell align="right">{usd(o.invoicedAmount)}</TableCell>
										<TableCell align="right">
											<Typography variant="body2" fontWeight={600}>
												{usd(
													target?.tipoOrden === 'FACTURAR'
														? o.pendienteFacturar
														: target?.tipoOrden === 'SALDO_A_FAVOR'
														? o.saldoAFavor
														: o.pendienteCobrar,
												)}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : null}

				{corte && (
					<Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
						Lista recortada: se muestran {corte.mostradas} de {corte.totales}. El importe de arriba es el total completo
						— filtrá por agencia o cliente para ver el resto del detalle.
					</Alert>
				)}

				<Stack spacing={0.5} mt={2}>
					<Typography variant="caption" color="text.disabled">
						Importes en dólares, a la fecha de corte {formatFecha(asOf)}.
					</Typography>
				</Stack>
			</Box>
		</Drawer>
	);
}
